import React, { useState, useEffect } from "react";
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import { ApiResponse } from "./VehicleView";
import { showSuccessAlert, showItemExistsError, showError, showConfirmSubmission } from '../../../../../core/alert/sweetAlertConfig';

interface RevenueLicence {
  id: number;
  numberPlate: string;
  revenueLicenceNumber: string;
  vehicleClass: string | null;
  fuelType: string | null;
  ownerName: string | null;
  ownerAddress: string | null;
  grossWeight: number;
  annualFee: number;
  arrears: number;
  fines: number;
  totalAmount: number;
  licenceStartDate: string | null;
  licenceEndDate: string | null;
  issuedDate: string | null;
  status: string | null;
  images: string[];
  createdBy: string | null;
  createdAt: string | null;
}

interface FormData {
  revenueLicenceNumber: string;
  vehicleClass: string;
  fuelType: string;
  ownerName: string;
  ownerAddress: string;
  grossWeight: string;
  annualFee: string;
  arrears: string;
  fines: string;
  totalAmount: string;
  licenceStartDate: string;
  licenceEndDate: string;
  issuedDate: string;
}

interface FormErrors {
  revenueLicenceNumber?: string;
  vehicleClass?: string;
  fuelType?: string;
  ownerName?: string;
  ownerAddress?: string;
  grossWeight?: string;
  annualFee?: string;
  arrears?: string;
  fines?: string;
  totalAmount?: string;
  licenceStartDate?: string;
  licenceEndDate?: string;
  issuedDate?: string;
  images?: string;
}

interface VehicleRevenueLicenceProps {
  show: boolean;
  onHide: () => void;
  numberPlate: string;
  vehicleClass: string | null;
  fuelType: string | null;
  ownerName: string | null;
  ownerAddress: string | null;
  grossWeight: number| null;
}

const VehicleRevenueLicence: React.FC<VehicleRevenueLicenceProps> = ({
  show,
  onHide,
  numberPlate,
  vehicleClass,
  fuelType,
  ownerName,
  ownerAddress,
  grossWeight,
}) => {
  const [licence, setLicence] = useState<RevenueLicence | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [history, setHistory] = useState<RevenueLicence[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    revenueLicenceNumber: '',
    vehicleClass: vehicleClass || '',
    fuelType: fuelType || '',
    ownerName: ownerName || '',
    ownerAddress: ownerAddress || '',
    grossWeight: grossWeight ? grossWeight.toString() : '',
    annualFee: '',
    arrears: '',
    fines: '',
    totalAmount: '',
    licenceStartDate: '',
    licenceEndDate: '',
    issuedDate: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [licenceImagePreviews, setLicenceImagePreviews] = useState<string[]>([]);
  const maxFileSizeMB = 5;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        timeZone: 'Asia/Colombo',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleAddModalOpen = () => {
    setShowAddModal(true);
    setFormErrors({});
    setFormData({
      revenueLicenceNumber: '',
      vehicleClass: vehicleClass || '',
      fuelType: fuelType || '',
      ownerName: ownerName || '',
      ownerAddress: ownerAddress || '',
      grossWeight: grossWeight ? grossWeight.toString() : '',
      annualFee: '',
      arrears: '',
      fines: '',
      totalAmount: '',
      licenceStartDate: '',
      licenceEndDate: '',
      issuedDate: '',
    });
    setFiles([]);
    setLicenceImagePreviews([]);
  };

  const handleAddModalClose = () => {
    setShowAddModal(false);
    setFormErrors({});
    licenceImagePreviews.forEach(URL.revokeObjectURL);
    setLicenceImagePreviews([]);
  };

  const handleHistoryModalOpen = async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await apiDefinitions.getRevenueLicenceHistory(numberPlate);
      const data: ApiResponse<RevenueLicence[]> = response.data;
      if (data.status === 'S1000' && data.data) {
        setHistory(data.data);
      } else {
        const message = data.statusDescription || `Failed to fetch revenue licence history (Status: ${data.status})`;
        setHistoryError(message);
        showError(message);
        setHistory([]);
      }
    } catch (err: any) {
      const message = err.response?.data?.statusDescription || err.message || 'Error fetching revenue licence history';
      setHistoryError(message);
      showError(message);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryModalClose = () => {
    setShowHistoryModal(false);
    setHistory([]);
    setHistoryError(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (['annualFee', 'arrears', 'fines'].includes(name)) {
        const annualFeeNum = parseFloat(newData.annualFee) || 0;
        const arrearsNum = parseFloat(newData.arrears) || 0;
        const finesNum = parseFloat(newData.fines) || 0;
        const newTotal = (annualFeeNum + arrearsNum + finesNum).toString();
        newData.totalAmount = newTotal;
      }
      return newData;
    });
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number,
    allowedTypes: string[]
  ) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles: File[] = [];
      const errors: FormErrors = {};

      selectedFiles.forEach((file) => {
        if (!allowedTypes.includes(file.type)) {
          errors.images = `Invalid file type for ${file.name}. Allowed types: JPEG, PNG, JPG.`;
          return;
        }
        if (file.size > maxSizeMB * 1024 * 1024) {
          errors.images = `File ${file.name} exceeds ${maxSizeMB}MB limit.`;
          return;
        }
        validFiles.push(file);
      });

      setFiles(validFiles);
      setFormErrors((prev) => ({ ...prev, images: errors.images || '' }));
      generatePreviews(validFiles, setLicenceImagePreviews);
    }
  };

  const generatePreviews = (files: File[], setPreviews: React.Dispatch<React.SetStateAction<string[]>>) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => {
      prev.forEach(URL.revokeObjectURL);
      return previews;
    });
  };

  const removeFile = (
    index: number,
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    previews: string[],
    setPreviews: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    updatedPreviews.forEach(URL.revokeObjectURL);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: FormErrors = {};

    // Existing field validations
    if (!formData.revenueLicenceNumber) errors.revenueLicenceNumber = 'Revenue Licence Number is required.';
    if (!formData.vehicleClass) errors.vehicleClass = 'Vehicle Class is required.';
    if (!formData.fuelType) errors.fuelType = 'Fuel Type is required.';
    if (!formData.ownerName) errors.ownerName = 'Owner Name is required.';
    if (!formData.ownerAddress) errors.ownerAddress = 'Owner Address is required.';
    if (!formData.grossWeight) errors.grossWeight = 'Gross Weight is required.';
    if (!formData.annualFee) errors.annualFee = 'Annual Fee is required.';
    if (!formData.licenceStartDate) errors.licenceStartDate = 'Licence Start Date is required.';
    if (!formData.licenceEndDate) errors.licenceEndDate = 'Licence End Date is required.';
    if (!formData.issuedDate) errors.issuedDate = 'Issued Date is required.';

    // Date validations
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day for comparison
    const issuedDate = formData.issuedDate ? new Date(formData.issuedDate) : null;
    const licenceStartDate = formData.licenceStartDate ? new Date(formData.licenceStartDate) : null;
    const licenceEndDate = formData.licenceEndDate ? new Date(formData.licenceEndDate) : null;

    if (issuedDate && issuedDate > today) {
      errors.issuedDate = 'Issued Date cannot be in the future.';
    }
    if (licenceStartDate && licenceStartDate > today) {
      errors.licenceStartDate = 'Licence Start Date cannot be in the future.';
    }
    if (licenceStartDate && licenceEndDate && licenceStartDate > licenceEndDate) {
      errors.licenceStartDate = 'Licence Start Date must be before or equal to Licence End Date.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const annualFeeNum = parseFloat(formData.annualFee) || 0;
    const arrearsNum = parseFloat(formData.arrears) || 0;
    const finesNum = parseFloat(formData.fines) || 0;
    const calculatedTotal = annualFeeNum + arrearsNum + finesNum;

    const isConfirmed = await showConfirmSubmission(calculatedTotal);

    if (isConfirmed) {
      try {
        setFormErrors({});
        const payload = {
          numberPlate,
          revenueLicenceNumber: formData.revenueLicenceNumber,
          vehicleClass: formData.vehicleClass,
          fuelType: formData.fuelType,
          ownerName: formData.ownerName,
          ownerAddress: formData.ownerAddress,
          grossWeight: parseFloat(formData.grossWeight),
          annualFee: annualFeeNum,
          arrears: arrearsNum,
          fines: finesNum,
          totalAmount: calculatedTotal,
          licenceStartDate: formData.licenceStartDate,
          licenceEndDate: formData.licenceEndDate,
          issuedDate: formData.issuedDate,
        };
        const formDataToSend = new FormData();
        formDataToSend.append('request', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
        files.forEach((file) => {
          const updatedFile = new File([file], file.name, { type: 'image/png', lastModified: file.lastModified });
          formDataToSend.append('images', updatedFile);
        });

        const response = await apiDefinitions.addVehRevenueLicence(formDataToSend);
        const data: ApiResponse<RevenueLicence> = response.data;
        if (data.status === 'S1000' && data.data) {
          showSuccessAlert("Revenue licence added successfully");
          setLicence(data.data);
          setShowAddModal(false);
          setFiles([]);
          setLicenceImagePreviews([]);
        } else if (data.statusDescription?.toLowerCase().includes('already exists')) {
          showItemExistsError("This revenue licence already exists.");
        } else {
          showSuccessAlert("Revenue licence added successfully");
        }
      } catch (err: any) {
        const message = err.response?.data?.statusDescription || err.message || 'Error adding revenue licence';
        if (message.toLowerCase().includes('already exists')) {
          showItemExistsError("This revenue licence already exists.");
        } else {
          showError(message);
        }
      }
    }
  };

  const downloadRevenueLicenceHistory = () => {
    const formattedData = history.map((item) => ({
      NumberPlate: item.numberPlate,
      RevenueLicenceNumber: item.revenueLicenceNumber || "N/A",
      VehicleClass: item.vehicleClass || "N/A",
      FuelType: item.fuelType || "N/A",
      OwnerName: item.ownerName || "N/A",
      OwnerAddress: item.ownerAddress || "N/A",
      GrossWeight: item.grossWeight,
      AnnualFee: item.annualFee,
      Arrears: item.arrears,
      Fines: item.fines,
      TotalAmount: item.totalAmount,
      LicenceStartDate: formatDate(item.licenceStartDate),
      LicenceEndDate: formatDate(item.licenceEndDate),
      IssuedDate: formatDate(item.issuedDate),
      Status: item.status || "N/A",
      CreatedBy: item.createdBy || "N/A",
      CreatedAt: formatDate(item.createdAt)
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RevenueLicenceHistory');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const fileData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(fileData, `Revenue_Licence_History_${numberPlate}.xlsx`);
  };

  useEffect(() => {
    const fetchLicence = async () => {
      if (!numberPlate) {
        setError('No number plate provided.');
        showError('No number plate provided.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const response = await apiDefinitions.getRevenueLicence(numberPlate);
        const data: ApiResponse<RevenueLicence> = response.data;
       
        if (data.status === 'S1000' && data.data) {
          setLicence(data.data);
        } else {
          const message = data.statusDescription || `Failed to fetch revenue licence (Status: ${data.status})`;
          setError(message);
          showError(message);
        }
      } catch (err: any) {
        const message = err.response?.data?.statusDescription || err.message || 'Error fetching revenue licence';
        setError(message);
        showError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchLicence();
  }, [numberPlate]);

  useEffect(() => {
    generatePreviews(files, setLicenceImagePreviews);
    return () => licenceImagePreviews.forEach(URL.revokeObjectURL);
  }, [files]);

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Revenue Licence - {numberPlate || 'N/A'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <div className="text-center">Loading...</div>}
          {error && <div className="alert alert-danger">{error}</div>}
          {!loading && !error && !licence && <div className="alert alert-info">No revenue licence found for this vehicle.</div>}
          {!loading && !error && licence && (
            <div className="row">
              <div className="col-md-6">
                <h5 className="mb-3">General Information</h5>
                <p><strong>ID:</strong> {licence.id}</p>
                <p><strong>Vehicle Class:</strong> {licence.vehicleClass || 'N/A'}</p>
                <p><strong>Fuel Type:</strong> {licence.fuelType || 'N/A'}</p>
                <p><strong>Owner Name:</strong> {licence.ownerName || 'N/A'}</p>
                <p><strong>Owner Address:</strong> {licence.ownerAddress || 'N/A'}</p>
                <p><strong>Gross Weight:</strong> {licence.grossWeight} kg</p>
                <p><strong>Annual Fee:</strong> Rs. {licence.annualFee.toLocaleString()}</p>
                <p><strong>Arrears:</strong> Rs. {licence.arrears.toLocaleString()}</p>
                <p><strong>Fines:</strong> Rs. {licence.fines.toLocaleString()}</p>
                <p><strong>Total Amount:</strong> Rs. {licence.totalAmount.toLocaleString()}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Revenue Licence Number:</strong> {licence.revenueLicenceNumber}</p>
                <p><strong>Status:</strong> {licence.status || 'N/A'}</p>
                <p><strong>Created By:</strong> {licence.createdBy || 'N/A'}</p>
                <p><strong>Created At:</strong> {formatDate(licence.createdAt)}</p>
                <h5 className="mb-3">Dates</h5>
                <p><strong>Licence Start Date:</strong> {formatDate(licence.licenceStartDate)}</p>
                <p><strong>Licence End Date:</strong> {formatDate(licence.licenceEndDate)}</p>
                <p><strong>Issued Date:</strong> {formatDate(licence.issuedDate)}</p>
                <h5>Images</h5>
                {licence.images && licence.images.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {licence.images.map((image, index) => (
                      <Button
                        key={`licence-image-${index}`}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleImageClick(image)}
                      >
                        View Image {index + 1}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <p>None</p>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="success" onClick={handleAddModalOpen}>
            Add Revenue Licence
          </Button>
          <Button variant="info" onClick={handleHistoryModalOpen}>
            View History
          </Button>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showImageModal} onHide={handleCloseImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>View Revenue Licence Image</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <img
              src={selectedImage}
              alt="Revenue Licence Document"
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
              onError={() => showError('Failed to load image.')}
            />
          ) : (
            <p>No image selected.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseImageModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAddModal} onHide={handleAddModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add Revenue Licence - {numberPlate}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleFormSubmit}>
            <div className="container">
              <div className="mb-4">
                <h5>Revenue Licence Records</h5>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Revenue Licence Number</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="revenueLicenceNumber"
                        value={formData.revenueLicenceNumber}
                        onChange={handleFormChange}
                        placeholder="Enter revenue licence number"
                      />
                      {formErrors.revenueLicenceNumber && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.revenueLicenceNumber}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Vehicle Class</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="vehicleClass"
                        value={formData.vehicleClass}
                        onChange={handleFormChange}
                        placeholder="Enter vehicle class"
                      />
                      {formErrors.vehicleClass && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.vehicleClass}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Fuel Type</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="fuelType"
                        value={formData.fuelType}
                        onChange={handleFormChange}
                        placeholder="Enter fuel type"
                      />
                      {formErrors.fuelType && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.fuelType}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Owner Name</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleFormChange}
                        placeholder="Enter owner name"
                      />
                      {formErrors.ownerName && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.ownerName}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Owner Address</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="ownerAddress"
                        value={formData.ownerAddress}
                        onChange={handleFormChange}
                        placeholder="Enter owner address"
                      />
                      {formErrors.ownerAddress && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.ownerAddress}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Gross Weight</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="grossWeight"
                        value={formData.grossWeight}
                        onChange={handleFormChange}
                        placeholder="Enter gross weight"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.grossWeight && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.grossWeight}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Annual Fee</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="annualFee"
                        value={formData.annualFee}
                        onChange={handleFormChange}
                        placeholder="Enter annual fee"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.annualFee && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.annualFee}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Arrears</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="arrears"
                        value={formData.arrears}
                        onChange={handleFormChange}
                        placeholder="Enter arrears"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.arrears && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.arrears}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Fines</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="fines"
                        value={formData.fines}
                        onChange={handleFormChange}
                        placeholder="Enter fines"
                        min="0"
                        step="0.01"
                      />
                      {formErrors.fines && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.fines}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Total Amount (Auto Calculated)</label>
                      <input
                        type="text"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="totalAmount"
                        value={formData.totalAmount}
                        readOnly
                      />
                      {formErrors.totalAmount && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.totalAmount}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Licence Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="licenceStartDate"
                        value={formData.licenceStartDate}
                        onChange={handleFormChange}
                        placeholder="Select licence start date"
                      />
                      {formErrors.licenceStartDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.licenceStartDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Licence End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="licenceEndDate"
                        value={formData.licenceEndDate}
                        onChange={handleFormChange}
                        placeholder="Select licence end date"
                      />
                      {formErrors.licenceEndDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.licenceEndDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="col-form-label mb-2">Issued Date</label>
                      <input
                        type="date"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        name="issuedDate"
                        value={formData.issuedDate}
                        onChange={handleFormChange}
                        placeholder="Select issued date"
                      />
                      {formErrors.issuedDate && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.issuedDate}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="mb-0">Revenue Licence Images (optional)</label>
                      <input
                        type="file"
                        className="form-control"
                        style={{ width: '100%', padding: '10px 15px' }}
                        multiple
                        accept={allowedTypes.join(',')}
                        onChange={(e) => handleFileChange(e, setFiles, maxFileSizeMB, allowedTypes)}
                      />
                      <small className="text-muted">Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG</small>
                      {formErrors.images && (
                        <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                          {formErrors.images}
                        </div>
                      )}
                      {files.length > 0 && (
                        <div className="mt-3">
                          <h6>Selected Files:</h6>
                          <div className="d-flex flex-wrap gap-2">
                            {files.map((file, index) => (
                              <div key={index} className="position-relative">
                                <img
                                  src={licenceImagePreviews[index]}
                                  alt={file.name}
                                  style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                  style={{ padding: '2px 6px' }}
                                  onClick={() =>
                                    removeFile(index, files, setFiles, licenceImagePreviews, setLicenceImagePreviews)
                                  }
                                >
                                  ×
                                </button>
                                <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {file.name}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <button type="submit" className="btn btn-success">
                Submit
              </button>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleAddModalClose}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistoryModal} onHide={handleHistoryModalClose} size="lg">
        <Modal.Header closeButton>
          <div className="d-flex align-items-center w-100">
            <Modal.Title>Revenue Licence History - {numberPlate}</Modal.Title>
            <div className="ms-auto me-5">
              <Button
                variant="success"
                onClick={downloadRevenueLicenceHistory}
                disabled={history.length === 0}
              >
                Download
              </Button>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {historyLoading && <div className="text-center">Loading...</div>}
          {historyError && <div className="alert alert-danger">{historyError}</div>}
          {!historyLoading && !historyError && history.length === 0 && (
            <div className="alert alert-info">No revenue licence history found for this vehicle.</div>
          )}
          {!historyLoading && !historyError && history.length > 0 && (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Revenue Licence Number</th>
                  <th>Vehicle Class</th>
                  <th>Licence Start Date</th>
                  <th>Licence End Date</th>
                  <th>Status</th>
                  <th>Images</th>
                </tr>
              </thead>
              <tbody>
                {history.map((record, index) => (
                  <tr key={`history-${index}`}>
                    <td>{record.revenueLicenceNumber || 'N/A'}</td>
                    <td>{record.vehicleClass || 'N/A'}</td>
                    <td>{formatDate(record.licenceStartDate)}</td>
                    <td>{formatDate(record.licenceEndDate)}</td>
                    <td>{record.status || 'N/A'}</td>
                    <td>
                      {record.images && record.images.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {record.images.map((image, imgIndex) => (
                            <Button
                              key={`history-image-${index}-${imgIndex}`}
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleImageClick(image)}
                            >
                              View Image {imgIndex + 1}
                            </Button>
                          ))}
                        </div>
                      ) : (
                        'None'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleHistoryModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VehicleRevenueLicence;